import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
	// Personal Information
	fullName: z.string().min(2, 'Name is required'),
	phone: z.string().min(10, 'Valid phone number required'),
	email: z.string().email('Valid email required'),
	company: z.string().min(2, 'Company name is required'),
	requestProposal: z.boolean().optional().default(false),

	// Organization Details
	industry: z.string().min(1, 'Industry is required'),
	address: z.string().min(1, 'Address is required'),

	// Training Details
	participants: z.string().optional(),
	customParticipants: z.string().optional(),
	participantLevel: z.string().optional(),
	customLevel: z.string().optional(),
	communicationSituations: z.array(z.string()).optional(),
	challenges: z.string().optional(),
	communicationGoals: z.array(z.string()).optional(),
	otherGoal: z.string().optional(),

	// Training Logistics
	trainingDate: z.string().optional(),
	trainingFormat: z.string().optional(),
	duration: z.string().optional(),
	customDuration: z.string().optional(),

	customLocation: z.string().optional(),

	// Additional Information
	additionalInfo: z.string().optional(),
	budget: z.string().optional(),
	referralSource: z.string().optional(),
	customReferral: z.string().optional(),
});
interface EnterpriseLeadFormProps {
	data: z.infer<typeof formSchema>;
	onSubmit: (data: z.infer<typeof formSchema>) => void;
	isSubmitting: boolean;
}

export function EnterpriseLeadForm({
	data,
	onSubmit,
	isSubmitting,
}: EnterpriseLeadFormProps) {
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			...data,
			requestProposal: data.requestProposal || false,
			communicationSituations: data.communicationSituations || [],
			communicationGoals: data.communicationGoals || [],
			trainingDate: data.trainingDate || '',
		},
	});

	// Watch values for conditional fields
	const requestProposal = form.watch('requestProposal');
	const participants = form.watch('participants');
	const participantLevel = form.watch('participantLevel');
	const duration = form.watch('duration');
	const referralSource = form.watch('referralSource');

	const industries = [
		'Technology',
		'Finance/Banking',
		'Healthcare',
		'Education',
		'Retail',
		'Manufacturing',
		'Government',
		'Non-profit',
		'Media/Entertainment',
		'Telecommunications',
		'Energy',
		'Transportation',
		'Legal',
		'Real Estate',
		'Other',
	];

	const participantOptions = ['10-15', '16-20', '21-25', 'Other'];

	const levelOptions = [
		'Entry Level',
		'Mid Level',
		'Senior Level',
		'Executive Level',
		'Other',
	];

	const communicationSituationOptions = [
		{ id: 'internal_meetings', label: 'Internal team meetings' },
		{ id: 'client_meetings', label: 'Client facing Meetings' },
		{ id: 'internal_presentations', label: 'Internal team Presentations' },
		{ id: 'client_presentations', label: 'Client facing presentations' },
		{ id: 'speeches', label: 'Speeches/ conferences' },
		{ id: 'traditional_media', label: 'Traditional Media' },
		{ id: 'digital_media', label: 'Digital Media' },
	];

	const communicationGoalOptions = [
		{
			id: 'business_acumen',
			label: 'Developing Corporate and Business Acumen',
		},
		{
			id: 'cultural_barriers',
			label: 'Overcoming Cultural and Language Barriers',
		},
		{
			id: 'anxiety',
			label: 'Overcoming Public Speaking Anxiety and Building Confidence',
		},
		{
			id: 'content_planning',
			label: 'Enhancing Presentation/Speech Content Planning and Structuring',
		},
		{
			id: 'storytelling',
			label: 'Mastering Storytelling Skills To Influence and Persuade audience',
		},
		{
			id: 'questions',
			label: 'Handling and Effectively Responding To Questions',
		},
		{
			id: 'delivery',
			label:
				'Developing Communication Performance and Delivery (Speech and Body Language Delivery)',
		},
		{ id: 'other', label: 'Other' },
	];

	const trainingFormatOptions = [
		'In-person',
		'Online (Zoom, Google Meets etc)',
	];

	const durationOptions = [
		'1 hour',
		'1x Half Day (4 hours)',
		'2x Half Days (8 hours)',
		'1x Full Day (8 hours)',
		'2x Full days (16 Hours)',
		'Other',
	];

	const referralOptions = [
		'LinkedIn',
		'Instagram',
		'Tik Tok',
		'Referral',
		'Other',
	];

	return (
		<div className='space-y-6 w-full max-w-3xl'>
			<SheetHeader className='p-0'>
				<SheetTitle>Request Consultation</SheetTitle>
				<p className='text-sm text-gray-500'>
					Please fill in details about your requirements to ensure we have a
					productive consultation session.
				</p>
			</SheetHeader>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
					<Accordion
						type='single'
						collapsible
						defaultValue='personal'
						className='w-full'
					>
						<AccordionItem value='personal'>
							<AccordionTrigger className='text-lg font-medium'>
								Personal Information
							</AccordionTrigger>
							<AccordionContent>
								<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
									<FormField
										control={form.control}
										name='fullName'
										render={({ field }) => (
											<FormItem>
												<FormLabel>Full Name*</FormLabel>
												<FormControl>
													<Input placeholder='John Doe' {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='email'
										render={({ field }) => (
											<FormItem>
												<FormLabel>Email*</FormLabel>
												<FormControl>
													<Input
														type='email'
														placeholder='john@example.com'
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='phone'
										render={({ field }) => (
											<FormItem>
												<FormLabel>Phone Number*</FormLabel>
												<FormControl>
													<Input
														type='tel'
														placeholder='+27 12 345 6789'
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='company'
										render={({ field }) => (
											<FormItem>
												<FormLabel>Company/Organization Name*</FormLabel>
												<FormControl>
													<Input placeholder='Company Inc.' {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='industry'
										render={({ field }) => (
											<FormItem className='col-span-2'>
												<FormLabel>
													What industry is your organisation in?*
												</FormLabel>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
												>
													<FormControl>
														<SelectTrigger>
															<SelectValue placeholder='Select your industry' />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{industries.map((industry) => (
															<SelectItem key={industry} value={industry}>
																{industry}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='address'
										render={({ field }) => (
											<FormItem className='col-span-2'>
												<FormLabel>Organisation address*</FormLabel>
												<FormDescription>
													Street, city, state/province, country, postal code
												</FormDescription>
												<FormControl>
													<Textarea
														placeholder='123 Main St, Johannesburg, Gauteng, South Africa, 2000'
														className='min-h-[80px]'
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name='requestProposal'
										render={({ field }) => (
											<FormItem className='col-span-2 flex flex-row items-start space-x-3 space-y-0'>
												<FormControl>
													<Checkbox
														checked={field.value}
														onCheckedChange={field.onChange}
													/>
												</FormControl>
												<div className='space-y-1 leading-none'>
													<FormLabel className='cursor-pointer'>
														Request a detailed training proposal
													</FormLabel>
													<FormDescription>
														Check this box if you would like to provide
														additional details for a complete training proposal
													</FormDescription>
												</div>
											</FormItem>
										)}
									/>
								</div>
							</AccordionContent>
						</AccordionItem>

						{requestProposal && (
							<>
								<AccordionItem value='training'>
									<AccordionTrigger className='text-lg font-medium'>
										Training Details
									</AccordionTrigger>
									<AccordionContent>
										<div className='grid grid-cols-1 gap-6'>
											<FormField
												control={form.control}
												name='participants'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															Pax: Number of delegates being trained*
														</FormLabel>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
														>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder='Select number range' />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{participantOptions.map((option) => (
																	<SelectItem key={option} value={option}>
																		{option}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>

											{participants === 'Other' && (
												<FormField
													control={form.control}
													name='customParticipants'
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																Kindly state the number of delegates if they are
																more than 25
															</FormLabel>
															<FormControl>
																<Input placeholder='e.g., 30' {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}

											<FormField
												control={form.control}
												name='participantLevel'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															At what level in the organisation are the
															delegates?*
														</FormLabel>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
														>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder='Select organizational level' />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{levelOptions.map((option) => (
																	<SelectItem key={option} value={option}>
																		{option}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>

											{participantLevel === 'Other' && (
												<FormField
													control={form.control}
													name='customLevel'
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																Please specify delegate level
															</FormLabel>
															<FormControl>
																<Input {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}

											<FormField
												control={form.control}
												name='communicationSituations'
												render={() => (
													<FormItem>
														<div className='mb-4'>
															<FormLabel>
																In which situations do the delegates regularly
																communicate in*
															</FormLabel>
															<FormDescription>
																Select all that apply
															</FormDescription>
														</div>
														<div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
															{communicationSituationOptions.map((item) => (
																<FormField
																	key={item.id}
																	control={form.control}
																	name='communicationSituations'
																	render={({ field }) => {
																		return (
																			<FormItem
																				key={item.id}
																				className='flex flex-row items-start space-x-3 space-y-0'
																			>
																				<FormControl>
																					<Checkbox
																						checked={field.value?.includes(
																							item.id,
																						)}
																						onCheckedChange={(checked) => {
																							return checked
																								? field.onChange([
																										...(field.value || []),
																										item.id,
																									])
																								: field.onChange(
																										field.value?.filter(
																											(value) =>
																												value !== item.id,
																										),
																									);
																						}}
																					/>
																				</FormControl>
																				<FormLabel className='font-normal cursor-pointer'>
																					{item.label}
																				</FormLabel>
																			</FormItem>
																		);
																	}}
																/>
															))}
														</div>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='challenges'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															Describe the current challenges you perceive are
															faced by the delegates*
														</FormLabel>
														<FormDescription>
															How have they affected performance and team/client
															rapport?
														</FormDescription>
														<FormControl>
															<Textarea
																placeholder='Please describe the challenges'
																className='min-h-[100px]'
																{...field}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='communicationGoals'
												render={() => (
													<FormItem>
														<div className='mb-4'>
															<FormLabel>
																What are your communication goals for the
																participants*
															</FormLabel>
															<FormDescription>
																Select all that apply
															</FormDescription>
														</div>
														<div className='grid grid-cols-1 gap-2'>
															{communicationGoalOptions.map((item) => (
																<FormField
																	key={item.id}
																	control={form.control}
																	name='communicationGoals'
																	render={({ field }) => {
																		return (
																			<FormItem
																				key={item.id}
																				className='flex flex-row items-start space-x-3 space-y-0'
																			>
																				<FormControl>
																					<Checkbox
																						checked={field.value?.includes(
																							item.id,
																						)}
																						onCheckedChange={(checked) => {
																							return checked
																								? field.onChange([
																										...(field.value || []),
																										item.id,
																									])
																								: field.onChange(
																										field.value?.filter(
																											(value) =>
																												value !== item.id,
																										),
																									);
																						}}
																					/>
																				</FormControl>
																				<FormLabel className='font-normal cursor-pointer'>
																					{item.label}
																				</FormLabel>
																			</FormItem>
																		);
																	}}
																/>
															))}
														</div>
														<FormMessage />
													</FormItem>
												)}
											/>

											{form.watch('communicationGoals')?.includes('other') && (
												<FormField
													control={form.control}
													name='otherGoal'
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																Please specify other communication goal
															</FormLabel>
															<FormControl>
																<Input {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}
										</div>
									</AccordionContent>
								</AccordionItem>

								<AccordionItem value='logistics'>
									<AccordionTrigger className='text-lg font-medium'>
										Training Logistics
									</AccordionTrigger>
									<AccordionContent>
										<div className='grid grid-cols-1 gap-6'>
											<FormField
												control={form.control}
												name='trainingDate'
												render={({ field }) => (
													<FormItem className='flex flex-col'>
														<FormLabel>
															What date(s) would you like the training*
														</FormLabel>
														<FormControl>
															<Input
																type='text'
																placeholder='DD/MM/YYYY'
																value={
																	field.value
																		? typeof field.value === 'string'
																			? field.value
																			: format(field.value, 'PPP')
																		: ''
																}
																onChange={(e) => {
																	// Store as string instead of Date object
																	field.onChange(e.target.value);
																}}
															/>
														</FormControl>
														<FormDescription>
															Please enter preferred date(s) for training
														</FormDescription>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='trainingFormat'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															What format of coaching do you require?*
														</FormLabel>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
														>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder='Select format' />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{trainingFormatOptions.map((option) => (
																	<SelectItem key={option} value={option}>
																		{option}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='duration'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															What is your preferred coaching duration?*
														</FormLabel>
														<FormDescription>
															Please note: the duration of the training will
															depend on your coaching goals. This question gives
															us an indication of how to break down the coaching
															based on your teams availability.
														</FormDescription>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
														>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder='Select duration' />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{durationOptions.map((option) => (
																	<SelectItem key={option} value={option}>
																		{option}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>

											{duration === 'Other' && (
												<FormField
													control={form.control}
													name='customDuration'
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																Please specify preferred duration
															</FormLabel>
															<FormControl>
																<Input {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}
										</div>
									</AccordionContent>
								</AccordionItem>

								<AccordionItem value='additional'>
									<AccordionTrigger className='text-lg font-medium'>
										Additional Information
									</AccordionTrigger>
									<AccordionContent>
										<div className='grid grid-cols-1 gap-6'>
											<FormField
												control={form.control}
												name='additionalInfo'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															Kindly share any other information / comments with
															us*
														</FormLabel>
														<FormDescription>
															This will help us better prepare your quote and
															coaching
														</FormDescription>
														<FormControl>
															<Textarea
																placeholder='Any other requirements or information'
																className='min-h-[100px]'
																{...field}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='budget'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															If you have a specific budget in mind, please feel
															free to share*
														</FormLabel>
														<FormDescription>
															Providing your budget helps us understand your
															needs and allows us to recommend the best options
															or refer you to someone who may be better suited
															for your requirements.
														</FormDescription>
														<FormControl>
															<Input
																placeholder='Budget amount or range'
																{...field}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name='referralSource'
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															We would love to find out where you heard about
															us*
														</FormLabel>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
														>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder='Select source' />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{referralOptions.map((option) => (
																	<SelectItem key={option} value={option}>
																		{option}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>

											{referralSource === 'Other' && (
												<FormField
													control={form.control}
													name='customReferral'
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																Please specify where you heard about us
															</FormLabel>
															<FormControl>
																<Input {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}
										</div>
									</AccordionContent>
								</AccordionItem>
							</>
						)}
					</Accordion>

					<div className='flex justify-end pt-6 border-t'>
						<Button
							type='submit'
							className='bg-[#fbb040] hover:bg-[#f8c880]'
							disabled={isSubmitting}
						>
							{isSubmitting ? (
								<>
									<Loader2 className='mr-2 h-4 w-4 animate-spin' />
									Submitting...
								</>
							) : (
								'Continue'
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
